import { Field, Form, Formik } from 'formik'
import { type NextPageWithLayout } from 'next'
import React from 'react'
import { ROUTE } from 'shared-constants'
import * as Yup from 'yup'
import { Button } from 'components/Button'
import { Input } from 'components/Input'
import { Box } from 'components/Layout'
import Link from 'components/Link'
import PasswordInput from 'components/PasswordInput'
import { Text } from 'components/Typography'
import { withGA4Provider } from 'hoc/withGoogleAnalytics'
import { useGoogleAnalytics } from 'providers/googleAnalytics'
import { GA4_PAGE_AREAS } from 'providers/googleAnalytics/types'
import { useUserAuth } from 'providers/userAuth'
import { USER_TYPE } from 'providers/userAuthUtil'
import { COLOR_INTENT, SPACE } from 'Theme'

const validationSchema = Yup.object().shape({
  password: Yup.string().label('Password').required(),
  email: Yup.string().email('Invalid email').trim().label('Email').required(),
})

const SignInExpertPage: NextPageWithLayout = () => {
  const userAuth = useUserAuth()
  const { executeDataToDataLayer } = useGoogleAnalytics()

  return (
    <Box mt={SPACE.PX_24}>
      <Formik
        initialValues={{ email: '', password: '' }}
        validationSchema={validationSchema}
        onSubmit={({ email, ...restValues }) =>
          userAuth.signIn({
            executeDataToDataLayer,
            userType: USER_TYPE.EXPERT,
            email: email.trim(),
            ...restValues,
          })
        }
      >
        {({ handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <Field name="email">
              {({ field, meta }) => (
                <Input
                  label="Email"
                  isDisabled={userAuth.isLoading}
                  isError={meta.touched && meta.error !== undefined}
                  hint={meta.touched ? meta.error : ''}
                  hasHint
                  {...field}
                />
              )}
            </Field>
            <Field name="password">
              {({ field, meta }) => (
                <PasswordInput
                  isDisabled={userAuth.isLoading}
                  isError={meta.touched && meta.error !== undefined}
                  hint={meta.touched ? meta.error : ''}
                  hasHint
                  labelActionLink={{
                    label: 'Reset password',
                    href: ROUTE.RESET_PASSWORD(),
                  }}
                  {...field}
                />
              )}
            </Field>
            <Button
              isDisabled={userAuth.isLoading}
              isLoading={userAuth.isLoading}
              type="submit"
            >
              Sign in
            </Button>
          </Form>
        )}
      </Formik>
      <Text
        as="p"
        color={COLOR_INTENT.SIGN_IN.TEXT}
        mt={SPACE.PX_22}
        textAlign="center"
      >
        New to The Expert?{' '}
        <Link href={ROUTE.TRADE()}>Apply for Trade membership.</Link>
      </Text>
    </Box>
  )
}

const SignInExpert = withGA4Provider({
  page_area: GA4_PAGE_AREAS.SIGN_IN_EXPERT,
})(SignInExpertPage)

export default SignInExpert
